
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";

import ReportPageShell from "../components/ReportPageShell";
import { Table, StatusBadge } from "../../../design-system";
import { ExportToolbarButton } from "../../../integrations/export";
import ListPageFilterBar from "../../../design-system/molecules/ListPageFilterBar";

import Config from "../../../../utils/config";
import Request from "../../../../utils/Request";
import {
  getParams,
  dateConvertWithoutTime,
} from "../../../../utils/common";

const DEFAULT_PAGE_SIZE = 10;

const filterConfig = [
  {
    name: "rrn",
    type: "text",
    value: "",
    placeholder: "RRN",
    title: "RRN",
  },
  {
    name: "cardRefNo",
    type: "text",
    value: "",
    placeholder: "Card Reference No.",
    title: "Card Ref No.",
  },
  {
    name: "fromDate",
    type: "date",
    value: "",
    placeholder: "From Date",
    title: "From Date",
    maxDate: new Date(),
  },
  {
    name: "toDate",
    type: "date",
    value: "",
    placeholder: "To Date",
    title: "To Date",
    maxDate: new Date(),
  },
  {
    name: "txnStatus",
    type: "select",
    value: "",
    placeholder: "Transaction Status",
    title: "Status",
    options: [
      { value: "SUCCESS", label: "Success" },
      { value: "FAILED", label: "Failed" },
    ],
    placeholderOnly: true,
  },
];

export default function OfflineTransactionsV2Page() {
  const [filters, setFilters] = useState(filterConfig);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [allForCsv, setAllForCsv] = useState([]);
  const [csvDisabled, setCsvDisabled] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const csvLinkRef = useRef(null);

  useEffect(() => {
    const fp = getParams(filters);

    const payload = {
      pagination: {
        pageNo: currentPage,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      rrn: fp.rrn || "",
      cardRefNo: fp.cardRefNo || "",
      txnStatus: fp.txnStatus?.value || fp.txnStatus || "",
      fromDate: fp.fromDate
        ? dateConvertWithoutTime(fp.fromDate)
        : "",
      toDate: fp.toDate
        ? dateConvertWithoutTime(fp.toDate)
        : "",
    };

    // Remove empty fields
    Object.keys(payload).forEach((key) => {
      if (!payload[key]) {
        delete payload[key];
      }
    });

    setLoading(true);

    const successHandler = (res) => {
      setLoading(false);

      setRows(
        res?.metaInfo?.data ||
          res?.data?.content ||
          []
      );

      setTotalPages(
        res?.metaInfo?.totalPages ||
          res?.data?.totalPages ||
          1
      );

      setTotalElements(
        res?.metaInfo?.totalElements ||
          res?.data?.totalElements ||
          0
      );
    };

    const errorHandler = (err) => {
      setLoading(false);

      toast.error(
        err?.message || "Failed to load transactions"
      );
    };

    const api = new Request(
      "",
      successHandler,
      errorHandler,
      false
    );

    api.post(
      Config.apis.admin.OFFLINE_TRANSACTION_REPORT,
      payload
    );
  }, [filters, currentPage, refreshNonce]);

  useEffect(() => {
    const fp = getParams(filters);

    const payload = {
      pagination: {
        pageNo: 1,
        pageSize: 5000,
      },
      rrn: fp.rrn || "",
      cardRefNo: fp.cardRefNo || "",
      txnStatus: fp.txnStatus?.value || fp.txnStatus || "",
      fromDate: fp.fromDate
        ? dateConvertWithoutTime(fp.fromDate)
        : "",
      toDate: fp.toDate
        ? dateConvertWithoutTime(fp.toDate)
        : "",
    };

    Object.keys(payload).forEach((key) => {
      if (!payload[key]) {
        delete payload[key];
      }
    });

    const successHandler = (res) => {
      setAllForCsv(
        res?.metaInfo?.data ||
          res?.data?.content ||
          []
      );
    };

    const api = new Request(
      "",
      successHandler,
      () => {},
      false
    );

    api.post(
      Config.apis.admin.OFFLINE_TRANSACTION_REPORT,
      payload
    );
  }, [filters]);


  const onFilterChange = useCallback((name, value) => {
    setFilters((prev) =>
      prev.map((f) =>
        f.name === name
          ? { ...f, value }
          : f
      )
    );
  }, []);

  const onSearch = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const onReset = useCallback(() => {
    setFilters(
      filterConfig.map((f) => ({
        ...f,
        value: "",
      }))
    );

    setCurrentPage(1);
  }, []);

  // ============================================================
  // CSV Headers
  // ============================================================

  const csvHeaders = [
    { label: "Corp ID", key: "corpId" },
    { label: "RRN", key: "rrn" },
    { label: "Amount", key: "txnAmt" },
    { label: "Transaction Type", key: "txnType" },
    { label: "Status", key: "txnStatus" },
    { label: "Date", key: "txnDate" },
    { label: "Time", key: "txnTime" },
    { label: "TID", key: "tid" },
    { label: "MCC", key: "mcc" },
    { label: "Narration", key: "narration" },
    { label: "Card Ref No", key: "cardRefNo" },
    { label: "Card Number", key: "cardNo" },
  ];

  // ============================================================
  // CSV Data
  // ============================================================

  const csvData = allForCsv.map((item) => ({
    corpId: item.corpId || "",
    rrn: item.rrn || "",
    txnAmt:
      item.txnAmt ||
      item.amount ||
      "",
    txnType: item.txnType || "",
    txnStatus: item.txnStatus || "",
    txnDate: item.txnDate || "",
    txnTime: item.txnTime || "",
    tid: item.tid || "",
    mcc: item.mcc || "",
    narration: item.narration || "",
    cardRefNo: item.cardRefNo || "",
    cardNo:
      item.cardNo ||
      item.maskedCardNo ||
      "",
  }));

  // ============================================================
  // CSV Download
  // ============================================================

  const CSV_COOLDOWN = 5000; // 5 seconds

const handleCsvDownload = async () => {
  // Prevent multiple clicks during download/cooldown
  if (csvDisabled) {
    return;
  }

  setCsvDisabled(true);

  try {
    let allData = [];
    let pageNo = 1;
    let totalPages = 1;

    const fp = getParams(filters);

    while (pageNo <= totalPages) {
      const payload = {
        pagination: {
          pageNo,
          pageSize: 100000,
        },
        rrn: fp.rrn || "",
        cardRefNo: fp.cardRefNo || "",
        txnStatus:
          fp.txnStatus?.value ||
          fp.txnStatus ||
          "",
        fromDate: fp.fromDate
          ? dateConvertWithoutTime(fp.fromDate)
          : "",
        toDate: fp.toDate
          ? dateConvertWithoutTime(fp.toDate)
          : "",
      };

      // Remove empty fields
      Object.keys(payload).forEach((key) => {
        if (!payload[key]) {
          delete payload[key];
        }
      });

      const result = await new Promise((resolve, reject) => {
        const api = new Request(
          "",
          (res) => resolve(res),
          (err) => reject(err),
          false
        );

        api.post(
          Config.apis.admin.OFFLINE_TRANSACTION_REPORT,
          payload
        );
      });

      const pageData =
        result?.metaInfo?.data ||
        result?.data?.content ||
        [];

      allData.push(...pageData);

      totalPages =
        result?.metaInfo?.totalPages ||
        result?.data?.totalPages ||
        1;

      pageNo++;
    }

    // Update CSV data
    setAllForCsv(allData);

    /*
     * Wait for React state to update CSVLink data,
     * then trigger the download.
     */
    setTimeout(() => {
      csvLinkRef.current?.link?.click();
    }, 200);

    /*
     * Keep button disabled for the configured cooldown.
     */
    setTimeout(() => {
      setCsvDisabled(false);
    }, CSV_COOLDOWN);

  } catch (err) {
    console.error("CSV download error:", err);

    toast.error(
      err?.message || "Failed to download CSV"
    );

    // Re-enable immediately if API/download preparation fails
    setCsvDisabled(false);
  }
};

  // ==================== Columns ====================
  const columns = [
    {
      key: "tid",
      label: "TID",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "corpId",
      label: "Corp ID",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "rrn",
      label: "RRN",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "txnAmt",
      label: "Amount",
      align: "left",
      render: (v) =>
        v
          ? `₹ ${parseFloat(v).toFixed(2)}`
          : "—",
    },
    {
      key: "mcc",
      label: "MCC",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "narration",
      label: "Narration",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "txnType",
      label: "Txn Type",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "txnStatus",
      label: "Status",
      align: "left",
      render: (v) =>
        v ? (
          <StatusBadge status={v} />
        ) : (
          "—"
        ),
    },
    {
      key: "txnDate",
      label: "Date",
      align: "left",
      render: (v, row) =>
        row.txnDate || "—",
    },
    {
      key: "txnTime",
      label: "Time",
      align: "left",
      render: (v, row) =>
        row.txnTime || "—",
    },
    {
      key: "cardRefNo",
      label: "Card Ref No.",
      align: "left",
      render: (v) => v || "—",
    },
    {
      key: "cardNo",
      label: "Card Number",
      align: "left",
      render: (v) => v || "—",
    },
  ];

  return (
    <ReportPageShell
      toolbar={
        <>
          <CSVLink
            ref={csvLinkRef}
            headers={csvHeaders}
            data={csvData}
            filename={`Offline_Transactions_${new Date()
              .toISOString()
              .slice(0, 10)}.csv`}
            style={{ display: "none" }}
          />

          <ExportToolbarButton
            disabled={csvDisabled}
            loading={csvDisabled}
            onClick={handleCsvDownload}
          >
            {csvDisabled ? "Downloading..." : "Download CSV"}
          </ExportToolbarButton>
        </>
      }
    >
      <ListPageFilterBar
        filters={filters}
        onFilterChange={onFilterChange}
        onSearch={onSearch}
        onReset={onReset}
      />

      <Table
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="No transactions found"
        rowKey={(row) =>
          row.txnId || row.id
        }
        pagination={
          totalPages > 1
            ? {
                currentPage,
                pageSize: DEFAULT_PAGE_SIZE,
                total: totalElements,
                onPageChange:
                  setCurrentPage,
              }
            : null
        }
      />
    </ReportPageShell>
  );
}